import React, { useCallback, useMemo, useState, useEffect } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  InputAdornment,
  InputHelperText,
  InputLabel,
  SelectField,
  TextField,
  Tooltip,
  Typography,
} from 'ui-kit';
import { currencyToSymbol, formatCurrency, getDigitsFromValue } from 'utils';
import ProductAddButton from '../ProductCardStore/ProductAddButton';
import { getOutOfStock } from '../utils';
import { productStoreValidationSchema } from './utils';
import { Checkbox } from 'components/Form/Input';
import { ProductExtraField, ProductExtraFieldValue } from 'api/types/product';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { DatePicker as AntdDatePicker } from 'antd';
import { RangePickerProps } from 'antd/lib/date-picker';
import moment from 'moment';
import _ from 'lodash';
import { EPriceType } from 'components/NewProductStepper/ProductBaseForm/constants';
import { IProductPrice } from 'api/product_sdk';
import { getTimeLabel } from 'components/NewProductStepper/ProductBaseForm/utils';

export interface InitialValues {
  amount: number;
  preorderBy: number;
  date: string;
  note: string;
  extraFields: ProductExtraFieldValue[];
  guestPrice: number;
  priceType: EPriceType;
  selectedProductPriceId?: number;
  // This is used to check if the selected product price is a variable price so that we can validate
  _productPriceIsVariablePrice?: boolean;
  _productShowDeliveryDate?: boolean;
}

interface Props {
  initialValues: InitialValues;
  isPreorder: boolean;
  isLocal: boolean;
  isLink?: boolean;
  currency: string;
  hasQuantity: boolean;
  quantity: number;
  price: string;
  onSubmit: (values: InitialValues) => void;
  extraFields: ProductExtraField[];
  priceType: EPriceType;
  productPrices: IProductPrice[];
  isEarlyCheckin?: boolean;
  isLateCheckout?: boolean;
  showDeliveryDate: boolean;
}

export default function ProductStoreForm({
  initialValues,
  currency,
  hasQuantity,
  quantity,
  price,
  isPreorder,
  isLocal,
  isLink,
  onSubmit,
  extraFields,
  priceType,
  productPrices,
  isEarlyCheckin,
  isLateCheckout,
  showDeliveryDate,
}: Props) {
  const { preorderBy } = initialValues;

  const [hasAgreed, setHasAgreed] = useState(false);
  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
    setValue,
  } = useForm({
    mode: 'onSubmit',
    defaultValues: { ...initialValues, _productShowDeliveryDate: showDeliveryDate },
    resolver: yupResolver(productStoreValidationSchema),
  });

  const amount = watch('amount');
  const selectedProductPriceId = watch('selectedProductPriceId');

  const outOfStock = getOutOfStock(hasQuantity, quantity, amount);

  const productPricesOptions = useMemo(
    () =>
      productPrices?.map((price) => {
        const optionPrice = price.isVariablePrice
          ? 'Variable price'
          : `${currencyToSymbol(currency) || currency} ${formatCurrency(price.price)}`;

        return {
          label: `${getTimeLabel({ value: price.timeLabel }) || price.label} - ${optionPrice}`,
          value: price.id,
        };
      }),
    [productPrices],
  );

  useEffect(() => {
    if (productPricesOptions?.length === 1) {
      setValue('selectedProductPriceId', productPricesOptions[0].value, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [productPricesOptions, setValue]);

  const selectedProductPriceIsVariablePrice = useMemo(() => {
    const selectedPrice = productPrices?.find((price) => price.id === selectedProductPriceId);

    setValue('_productPriceIsVariablePrice', selectedPrice?.isVariablePrice, {
      shouldValidate: true,
    });

    return selectedPrice?.isVariablePrice;
  }, [productPrices, selectedProductPriceId]);

  const disabledDate: RangePickerProps['disabledDate'] = useCallback(
    (current) => {
      const currentStart = moment(current).startOf('day'); // Start of the current day

      return preorderBy === 0
        ? currentStart < moment().startOf('day')
        : currentStart < moment().add(preorderBy, 'days').startOf('day');
    },
    [preorderBy],
  );

  return (
    <form className="ProductModal-container-content-form" onSubmit={handleSubmit(onSubmit)}>
      {showDeliveryDate && (
        <Controller
          control={control}
          name="date"
          render={({ field: { value, onChange }, fieldState }) => (
            <div style={{ color: fieldState.error?.message ? 'red' : 'inherit' }}>
              <InputLabel
                label="Date during your reservation you plan to use this service"
                required
              />
              <AntdDatePicker
                inputReadOnly
                allowClear={false}
                value={value ? moment(value) : undefined}
                onChange={onChange}
                size="large"
                disabledDate={disabledDate}
                format="MM/DD/YYYY"
                placeholder="MM/DD/YYYY"
                style={{
                  borderColor: fieldState.error?.message ? 'red' : 'inherit',
                }}
                defaultPickerValue={moment()}
              />
              {Boolean(fieldState.error?.message) && (
                <InputHelperText type="error">{fieldState.error?.message}</InputHelperText>
              )}
              {!_.isNull(preorderBy) && preorderBy > 0 && (
                <InputHelperText>
                  This item needs to be ordered{' '}
                  {preorderBy > 1 ? `${preorderBy} days` : `${preorderBy} day`} in advance
                </InputHelperText>
              )}
            </div>
          )}
        />
      )}
      <div className="ProductModal-container-content__details">
        {!isLink && (
          <div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              {priceType === EPriceType.VARIABLE && (
                <Controller
                  control={control}
                  name="guestPrice"
                  render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
                    <TextField
                      inputRef={ref}
                      label={
                        <div>
                          Add your payment amount{' '}
                          <Tooltip title="Minimum .50¢ due to processing fees">
                            <ExclamationCircleOutlined />
                          </Tooltip>
                        </div>
                      }
                      InputProps={{
                        startAdornment: <InputAdornment>$</InputAdornment>,
                      }}
                      placeholder="0.00"
                      value={value}
                      onChange={(e) => onChange(getDigitsFromValue(e.target.value))}
                      error={!!error}
                      errorMessage={error ? error?.message : null}
                    />
                  )}
                />
              )}
              {priceType === EPriceType.STATIC && (
                <Typography
                  variant="body2"
                  color="secondary"
                  className="ProductModal-container-content__details-price"
                >
                  {currencyToSymbol(currency) || `${currency}`} {formatCurrency(+price * amount)}
                </Typography>
              )}
              {priceType === EPriceType.MULTIPLE && (
                <Controller
                  control={control}
                  name="selectedProductPriceId"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <SelectField
                      fullWidth
                      required
                      label="Select Option"
                      placeholder="Select option"
                      options={productPricesOptions}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      errorMessage={error ? error?.message : null}
                    />
                  )}
                />
              )}
              {!isEarlyCheckin && !isLateCheckout && (
                <Controller
                  control={control}
                  name="amount"
                  render={({ field: { onChange, value } }) => (
                    <ProductAddButton
                      amount={value}
                      outOfStock={outOfStock}
                      onSubstract={() => onChange(Math.max(value - 1, 0))}
                      onAdd={() => onChange(value + 1)}
                      initialShow
                    />
                  )}
                />
              )}
            </div>
            {selectedProductPriceIsVariablePrice && (
              <Controller
                control={control}
                name="guestPrice"
                render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
                  <TextField
                    inputRef={ref}
                    label={
                      <div>
                        Add your payment amount{' '}
                        <Tooltip title="Minimum .50¢ due to processing fees">
                          <ExclamationCircleOutlined />
                        </Tooltip>
                      </div>
                    }
                    InputProps={{
                      startAdornment: <InputAdornment>$</InputAdornment>,
                    }}
                    placeholder="0.00"
                    value={value}
                    onChange={(e) => onChange(getDigitsFromValue(e.target.value))}
                    error={!!error}
                    errorMessage={error ? error?.message : null}
                  />
                )}
              />
            )}
          </div>
        )}
      </div>
      <section>
        {extraFields?.map(({ id, label }, index) => (
          <Controller
            key={index}
            control={control}
            name={`extraFields.${index}`}
            render={({ field: { value, ref, onChange, ...rest }, fieldState: { error } }) => {
              const valueError = error ? _.get(error, 'value') : null;

              return (
                <TextField
                  className="ProductModal-container-content-form__description"
                  label={
                    <span className="ProductModal-container-content-form__description-label">
                      {label}
                    </span>
                  }
                  fullWidth
                  inputRef={ref}
                  value={value?.value ?? ''}
                  required
                  onChange={(e) =>
                    onChange({
                      productField: id,
                      value: e.target.value,
                    })
                  }
                  {...rest}
                  error={!!valueError}
                  // The error message is attached to the value from {productField, value}
                  errorMessage={valueError ? _.get(valueError, 'message', '') : null}
                />
              );
            }}
          />
        ))}

        {(isLocal || isPreorder) && (
          <Controller
            control={control}
            name="note"
            render={({ field: { value, ref, ...rest } }) => (
              <TextField
                className="ProductModal-container-content-form__description"
                label={
                  <span className="ProductModal-container-content-form__description-label">
                    Additional Notes
                  </span>
                }
                fullWidth
                rows={4}
                multiline
                placeholder="Parking instructions, time preferences, allergies etc."
                inputRef={ref}
                value={value ?? ''}
                {...rest}
              />
            )}
          />
        )}
      </section>
      {isPreorder && (
        <div className="ProductModal-container-content-form__descriptionAgreement">
          <Checkbox
            className="ProductModal-container-content-form__descriptionAgreement-checkbox"
            checked={hasAgreed}
            onChange={() => setHasAgreed((previous) => !previous)}
          />
          <Typography
            variant="subtitle2"
            className="ProductModal-container-content-form__descriptionAgreement-label"
          >
            I have read and agreed to this description.
          </Typography>
        </div>
      )}

      {isLocal && (
        <div>
          Prices are based on average vendor rates. You will never be charged more than this
          displayed price without your approval. If the final price is higher than this rate, this
          request won’t move forward, and you will not be charged. Our team will contact you, and
          you can choose whether to start a new request at the updated price.
        </div>
      )}

      <Button
        variant="contained"
        color="textDark"
        disabled={(!hasAgreed && isPreorder) || isSubmitting}
        type="submit"
        fullWidth
      >
        {isLink ? 'View site' : initialValues.amount > 1 ? 'Save changes' : 'Add to cart'}
      </Button>
    </form>
  );
}
